import React, { useState, useRef, useEffect } from "react";
import { Button, Typography, Space } from "antd";
import {
  ShoppingOutlined,
  ArrowRightOutlined,
  PlayCircleOutlined,
  SoundOutlined
} from "@ant-design/icons";

const { Title, Text } = Typography;

interface HeroBannerProps {
  bannerImage?: string;
  storeName: string;
  storeDescription?: string;
  primaryColor?: string;
  secondaryColor?: string;
  fontFamily?: string;
  onShopNow?: () => void;
  onExplore?: () => void;
}

const HeroBanner: React.FC<HeroBannerProps> = ({
  bannerImage,
  storeName,
  storeDescription,
  primaryColor = "#1890ff",
  secondaryColor = "#52c41a",
  fontFamily = "Inter",
  onShopNow,
  onExplore,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  // Animation d'entrée progressive
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Intersection Observer pour animations au scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.2 }
    );

    if (bannerRef.current) {
      observer.observe(bannerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  if (!bannerImage || imageError) {
    return null; // Pas d'affichage si pas de bannière configurée
  }

  const gradientOverlay = `linear-gradient(
    135deg,
    rgba(0,0,0,0.4) 0%,
    rgba(0,0,0,0.2) 50%,
    rgba(0,0,0,0.6) 100%
  )`;

  const modernGradient = `linear-gradient(
    135deg,
    ${primaryColor}15 0%,
    ${secondaryColor}10 100%
  )`;

  return (
    <div
      ref={bannerRef}
      className="hero-banner"
      style={{
        position: "relative",
        width: "100%",
        maxWidth: "100vw",
        height: "auto",
        minHeight: 0,
        maxHeight: "none",
        aspectRatio: "12 / 5",
        overflow: "hidden",
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        fontFamily: `${fontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
        transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
        transform: isVisible ? "translateY(0)" : "translateY(20px)",
        opacity: isVisible ? 1 : 0,
      }}
    >
      {/* Image de fond avec lazy loading */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: imageLoaded
            ? `${gradientOverlay}, url(${bannerImage})`
            : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
          transition: "all 1.2s ease-in-out",
          transform: imageLoaded ? "scale(1)" : "scale(1.05)",
          filter: imageLoaded ? "brightness(0.9)" : "brightness(0.7)",
        }}
      >
        <img
          src={bannerImage}
          alt="Banner"
          style={{ display: "none" }}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
        />
      </div>

      {/* Overlay moderne avec glassmorphism */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: modernGradient,
          backdropFilter: "blur(1px)",
        }}
      />

      {/* Particules décoratives animées */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, ${primaryColor}20 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, ${secondaryColor}20 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, ${primaryColor}10 0%, transparent 50%)
          `,
          animation: "float 6s ease-in-out infinite",
        }}
      />

      {/* Contenu principal */}
      <div
        style={{
          position: "relative",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          textAlign: "center",
          zIndex: 10,
        }}
      >
        <div
          style={{
            maxWidth: "800px",
            width: "100%",
            animation: isVisible ? "slideInUp 0.8s ease-out" : undefined,
          }}
        >
          {/* Badge moderne en haut */}
          <div
            style={{
              display: "inline-block",
              background: "rgba(255, 255, 255, 0.15)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "50px",
              padding: "8px 20px",
              marginBottom: "2rem",
              color: "white",
              fontSize: "12px",
              fontWeight: "600",
              textTransform: "uppercase",
              letterSpacing: "1px",
              animation: "fadeInDown 0.6s ease-out 0.2s both",
            }}
          >
            <SoundOutlined style={{ marginRight: 8 }} />
            Nouvelle Collection
          </div>

          {/* Titre principal */}
          <Title
            level={1}
            style={{
              color: "white",
              margin: "0 0 1.5rem 0",
              fontSize: "clamp(2.5rem, 5vw, 4rem)",
              fontWeight: "800",
              lineHeight: "1.1",
              textShadow: "0 4px 20px rgba(0,0,0,0.3)",
              background: `linear-gradient(135deg, white 0%, ${primaryColor}20 100%)`,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              animation: "fadeInUp 0.8s ease-out 0.4s both",
            }}
          >
            Bienvenue chez {storeName}
          </Title>

          {/* Description */}
          {storeDescription && (
            <Text
              style={{
                color: "rgba(255, 255, 255, 0.95)",
                fontSize: "1.25rem",
                fontWeight: "400",
                lineHeight: "1.6",
                marginBottom: "3rem",
                display: "block",
                maxWidth: "600px",
                margin: "0 auto 3rem auto",
                textShadow: "0 2px 10px rgba(0,0,0,0.2)",
                animation: "fadeInUp 0.8s ease-out 0.6s both",
              }}
            >
              {storeDescription}
            </Text>
          )}

          {/* CTA Buttons modernes */}
          <Space
            size="large"
            wrap
            style={{
              animation: "fadeInUp 0.8s ease-out 0.8s both",
            }}
          >
            <Button
              type="primary"
              size="large"
              icon={<ShoppingOutlined />}
              onClick={onShopNow}
              style={{
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                border: "none",
                borderRadius: "50px",
                height: "60px",
                padding: "0 40px",
                fontSize: "16px",
                fontWeight: "600",
                boxShadow: "0 8px 25px rgba(0,0,0,0.15), 0 4px 10px rgba(0,0,0,0.1)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
              onMouseEnter={(e) => {
                const target = e.currentTarget as HTMLElement;
                target.style.transform = "translateY(-3px) scale(1.05)";
                target.style.boxShadow = "0 12px 35px rgba(0,0,0,0.2), 0 8px 15px rgba(0,0,0,0.15)";
              }}
              onMouseLeave={(e) => {
                const target = e.currentTarget as HTMLElement;
                target.style.transform = "translateY(0) scale(1)";
                target.style.boxShadow = "0 8px 25px rgba(0,0,0,0.15), 0 4px 10px rgba(0,0,0,0.1)";
              }}
            >
              Découvrir la boutique
            </Button>

            <Button
              type="default"
              size="large"
              icon={<ArrowRightOutlined />}
              onClick={onExplore}
              style={{
                background: "rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(10px)",
                border: "2px solid rgba(255, 255, 255, 0.3)",
                borderRadius: "50px",
                height: "60px",
                padding: "0 40px",
                fontSize: "16px",
                fontWeight: "600",
                color: "white",
                boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
              onMouseEnter={(e) => {
                const target = e.currentTarget as HTMLElement;
                target.style.background = "rgba(255, 255, 255, 0.25)";
                target.style.border = "2px solid rgba(255, 255, 255, 0.5)";
                target.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                const target = e.currentTarget as HTMLElement;
                target.style.background = "rgba(255, 255, 255, 0.15)";
                target.style.border = "2px solid rgba(255, 255, 255, 0.3)";
                target.style.transform = "translateY(0)";
              }}
            >
              Explorer les produits
            </Button>
          </Space>
        </div>
      </div>

      {/* Indicateur de scroll moderne */}
      <div
        style={{
          position: "absolute",
          bottom: "30px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10,
          animation: "bounce 2s infinite",
        }}
      >
        <div
          style={{
            width: "2px",
            height: "40px",
            background: "rgba(255, 255, 255, 0.6)",
            margin: "0 auto 10px",
            borderRadius: "2px",
          }}
        />
        <div
          style={{
            width: "6px",
            height: "6px",
            background: "white",
            borderRadius: "50%",
            margin: "0 auto",
            boxShadow: "0 0 10px rgba(255,255,255,0.5)",
          }}
        />
      </div>

      {/* Styles CSS intégrés pour les animations */}
      <style>{`
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% {
            transform: translateX(-50%) translateY(0);
          }
          40%, 43% {
            transform: translateX(-50%) translateY(-10px);
          }
          70% {
            transform: translateX(-50%) translateY(-5px);
          }
          90% {
            transform: translateX(-50%) translateY(-3px);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          33% {
            transform: translateY(-10px) rotate(1deg);
          }
          66% {
            transform: translateY(5px) rotate(-1deg);
          }
        }

        .hero-banner.animate-in {
          transform: translateY(0) !important;
          opacity: 1 !important;
        }

        .hero-banner {
          width: 100%;
          max-width: 100vw;
          display: block;
          overflow: hidden;
          contain: paint;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .hero-banner {
            height: 42vh !important;
            min-height: 220px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default HeroBanner;
